import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { Spinner } from "@/components/Spinner";
import { PageSkeleton } from "@/components/Skeletons";
import { EmptyState } from "@/components/EmptyState";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DW_CATEGORIES, DW_ORDER } from "./deepWithinConfig";
import { getPracticeConfigs, PILLARS } from "./practices/practiceConfigs";
import { Sparkles, BookOpen, Plus, ChevronRight, CheckCircle2, Activity, Flame, TrendingUp, Trophy, BarChart3 } from "lucide-react";

const PILLAR_COLOR = { realize: "#5E7A73", designate: "#7A6A5E", release: "#8A6B7A" };

function HeroTile({ to, title, subtitle, icon: Icon, tint, testId }) {
  return (
    <Link to={to} data-testid={testId}
      className="group relative flex flex-1 flex-col gap-3 overflow-hidden rounded-3xl border p-6 transition-all hover:-translate-y-0.5 hover:shadow-md"
      style={{ borderColor: tint + "55", backgroundColor: tint + "14" }}>
      <div className="flex items-center justify-between">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ backgroundColor: tint + "26" }}>
          <Icon className="h-6 w-6" style={{ color: tint }} />
        </span>
        <ChevronRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </div>
      <div>
        <h3 className="font-display text-xl text-foreground">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </Link>
  );
}

function PracticeTab({ t, summary }) {
  const totals = summary.totals || { in_progress: 0, transformed: 0 };
  const buckets = summary.buckets || {};
  const showHero = (totals.in_progress || 0) > 0 || (totals.transformed || 0) > 0;
  return (
    <div className="space-y-6">
      {showHero && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3" data-testid="dw-progress-hero">
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <Activity className="h-4 w-4 text-primary" />
            <strong className="font-display text-base text-foreground" data-testid="dw-hero-in-progress">{totals.in_progress || 0}</strong> {t("dw.in_progress")}
          </span>
          <span className="h-4 w-px bg-border" />
          <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <strong className="font-display text-base text-emerald-600" data-testid="dw-hero-transformed">{totals.transformed || 0}</strong> {t("dw.transformed")}
          </span>
          {(() => {
            const ip = totals.in_progress || 0;
            const tr = totals.transformed || 0;
            const tot = ip + tr;
            if (tot === 0) return null;
            const pct = Math.round((tr / tot) * 100);
            return (
              <div className="w-full" data-testid="dw-progress-bar">
                <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{t("dw.progress_label", { done: tr, total: tot })}</span>
                  <span className="font-medium text-foreground">{pct}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })()}
        </div>
      )}

      <div className="flex flex-col gap-4 sm:flex-row">
        <HeroTile to="/app/practices" testId="dw-hero-practices" title={t("dw.hero_practices_title")} subtitle={t("dw.hero_practices_sub")} icon={Sparkles} tint="#B87F3A" />
        <HeroTile to="/app/journal" testId="dw-hero-journal" title={t("dw.hero_journal_title")} subtitle={t("dw.hero_journal_sub")} icon={BookOpen} tint="#3E6478" />
      </div>

      <div className="lg:hidden">
        <h2 className="mb-3 font-display text-lg text-foreground">{t("dw.buckets_heading")}</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {DW_ORDER.map((key) => {
            const c = DW_CATEGORIES[key];
            const Icon = c.icon;
            const b = buckets[c.bucket] || {};
            const total = b.total || 0;
            return (
              <Link key={key} to={`/app/within/${c.slug}`} data-testid={`dw-bucket-${key}`}
                className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/30">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: c.accent + "22" }}>
                  <Icon className="h-5 w-5" style={{ color: c.accent }} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-foreground">{t(`dw.${key}.title`)}</p>
                  <p className="truncate text-xs text-muted-foreground">{total > 0 ? t("dw.entries", { n: total }) : t(`dw.${key}.sub`)}</p>
                </div>
                <span className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors group-hover:border-primary group-hover:text-primary">
                  <Plus className="h-4 w-4" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function StatCardMini({ icon: Icon, label, value, tint }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ backgroundColor: (tint || "#5E7A73") + "22" }}>
        <Icon className="h-5 w-5" style={{ color: tint || "#5E7A73" }} />
      </span>
      <p className="mt-2 font-display text-2xl text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function InsightsTab({ t }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/practices/deep-within-insights?days=30").then(({ data }) => setData(data)).catch(() => setData({})); }, []);
  if (data === null) return <Spinner label={t("common.loading")} />;
  if (!data.total_entries) return <EmptyState icon={BarChart3} title={t("dw.insights_empty_title")} description={t("dw.insights_empty_sub")} />;

  const mood = data.mood || {};
  const streaks = data.streaks || {};
  const series = data.series_7d || [];
  const maxSeries = Math.max(1, ...series.map((s) => s.practices || 0));
  const pillars = data.by_pillar || {};
  const maxPillar = Math.max(1, ...Object.values(pillars));
  const configs = getPracticeConfigs(t);

  return (
    <div className="space-y-6" data-testid="dw-insights">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCardMini icon={BarChart3} label={t("dw.insights_total")} value={data.total_entries} tint="#5E7A73" />
        <StatCardMini icon={Flame} label={t("dw.insights_streak_current")} value={streaks.current || 0} tint="#B87F3A" />
        <StatCardMini icon={Trophy} label={t("dw.insights_streak_longest")} value={streaks.longest || 0} tint="#7A6A5E" />
        <StatCardMini icon={TrendingUp} label={t("dw.insights_mood_delta")} value={mood.avg_delta != null ? (mood.avg_delta > 0 ? `+${mood.avg_delta}` : mood.avg_delta) : "—"} tint="#8A6B7A" />
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="mb-3 font-display text-base text-foreground">{t("dw.insights_7d")}</h3>
        <div className="flex items-end justify-between gap-1.5" style={{ height: 120 }}>
          {series.map((s, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1.5">
              <div className="flex w-full items-end" style={{ height: 90 }}>
                <div className="w-full rounded-t-md bg-primary/80 transition-all" style={{ height: `${((s.practices || 0) / maxSeries) * 100}%`, minHeight: s.practices ? 4 : 0 }} />
              </div>
              <span className="text-[10px] text-muted-foreground">{new Date(s.date).toLocaleDateString(undefined, { weekday: "short" }).slice(0, 2)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="mb-3 font-display text-base text-foreground">{t("dw.insights_by_pillar")}</h3>
        <div className="space-y-2.5">
          {["realize", "designate", "release"].map((p) => (
            <div key={p} className="flex items-center gap-3">
              <span className="w-24 text-sm text-muted-foreground">{t(`pillar_${p}`)}</span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-secondary">
                <div className="h-full rounded-full" style={{ width: `${((pillars[p] || 0) / maxPillar) * 100}%`, backgroundColor: PILLAR_COLOR[p] }} />
              </div>
              <span className="w-8 text-right font-display text-sm text-foreground">{pillars[p] || 0}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-3 font-display text-base text-foreground">{t("dw.insights_mood")}</h3>
          <div className="flex items-center justify-around">
            <div className="text-center"><p className="font-display text-2xl text-foreground">{mood.avg_before ?? "—"}</p><p className="text-xs text-muted-foreground">{t("dw.insights_mood_before")}</p></div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
            <div className="text-center"><p className="font-display text-2xl text-emerald-600">{mood.avg_after ?? "—"}</p><p className="text-xs text-muted-foreground">{t("dw.insights_mood_after")}</p></div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="mb-2 font-display text-base text-foreground">{t("dw.insights_sources")}</h3>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.sources || {}).filter(([, n]) => n > 0).map(([k, n]) => (
              <span key={k} className="rounded-full border border-border bg-secondary/40 px-2.5 py-1 text-xs text-muted-foreground">{t(`dw.src_${k}`)}: {n}</span>
            ))}
          </div>
          {data.top_practice ? <p className="mt-3 text-sm text-muted-foreground">{t("dw.insights_top")}: <strong className="text-foreground">{configs[data.top_practice]?.title || data.top_practice}</strong></p> : null}
        </div>
      </div>
    </div>
  );
}

function HistoryTab({ t }) {
  const [entries, setEntries] = useState(null);
  const configs = getPracticeConfigs(t);
  const iconFor = {};
  PILLARS.forEach((p) => p.practices.forEach((pr) => { iconFor[pr.key] = pr.icon; }));
  useEffect(() => { api.get("/practices/entries?limit=20").then(({ data }) => setEntries(Array.isArray(data) ? data : [])).catch(() => setEntries([])); }, []);
  if (entries === null) return <Spinner label={t("common.loading")} />;
  if (entries.length === 0) return <EmptyState icon={Activity} title={t("dw.history_empty_title")} description={t("dw.history_empty_sub")} action={<Link to="/app/practices" className="text-sm font-medium text-primary hover:underline">{t("practices_hub_title")}</Link>} />;

  return (
    <div className="space-y-3" data-testid="dw-history">
      <div className="flex justify-end">
        <Link to="/app/practices/history" className="text-sm font-medium text-primary hover:underline" data-testid="dw-history-full">{t("dw.history_full_log")}</Link>
      </div>
      {entries.map((e) => {
        const Icon = iconFor[e.practice_type] || Activity;
        const color = PILLAR_COLOR[e.pillar] || "#5E7A73";
        return (
          <div key={e.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: color + "22" }}>
              <Icon className="h-5 w-5" style={{ color }} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-medium text-foreground">{configs[e.practice_type]?.title || e.practice_type}</p>
              <p className="text-xs text-muted-foreground">{shortDate(e.created_at)}</p>
            </div>
            {typeof e.mood_before === "number" && typeof e.mood_after === "number" ? (
              <span className="rounded-full bg-secondary/60 px-2.5 py-1 text-xs text-muted-foreground">{e.mood_before} → {e.mood_after}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default function DeepWithin() {
  const t = useT();
  usePageMeta(t("dw.title"));
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("practice");

  useEffect(() => { api.get("/deep-within/summary").then(({ data }) => setSummary(data || {})).catch(() => setSummary({})); }, []);
  if (summary === null) return <PageSkeleton />;

  return (
    <div data-testid="deep-within-screen">
      <PageHeader eyebrow={t("nav.experience.journey")} title={t("dw.title")} subtitle={t("dw.subtitle")} />
      <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8">
        <aside className="hidden lg:block" data-testid="dw-rail">
          <div className="sticky top-24 space-y-5">
            <div>
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("rw3.dw_areas")}</p>
              <div className="space-y-0.5">
                {DW_ORDER.map((key) => {
                  const c = DW_CATEGORIES[key];
                  const Icon = c.icon;
                  const b = (summary.buckets || {})[c.bucket] || {};
                  const ip = b.in_progress || 0;
                  const tr = b.transformed || 0;
                  return (
                    <Link key={key} to={`/app/within/${c.slug}`} data-testid={`dw-rail-${key}`}
                      className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: c.accent + "22" }}>
                        <Icon className="h-4 w-4" style={{ color: c.accent }} />
                      </span>
                      <span className="flex-1 truncate">{t(`dw.${key}.title`)}</span>
                      {(ip || tr) ? (
                        <span className="ml-1 flex shrink-0 items-center gap-1.5 text-[11px] font-medium" data-testid={`dw-rail-count-${key}`}>
                          {ip ? <span className="text-muted-foreground" title={t("dw.in_progress")}>{ip}</span> : null}
                          {tr ? <span className="inline-flex items-center gap-0.5 text-emerald-600" title={t("dw.transformed")}><CheckCircle2 className="h-3 w-3" />{tr}</span> : null}
                        </span>
                      ) : null}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("rw3.dw_quick")}</p>
              <div className="space-y-0.5">
                <Link to="/app/practices" data-testid="dw-rail-practices" className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                  <Sparkles className="h-4 w-4 shrink-0 text-primary" /><span className="truncate">{t("nav.practices")}</span>
                </Link>
                <Link to="/app/journal" data-testid="dw-rail-journal" className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                  <BookOpen className="h-4 w-4 shrink-0 text-primary" /><span className="truncate">{t("nav.journal")}</span>
                </Link>
              </div>
            </div>
          </div>
        </aside>
        <div className="mt-6 lg:mt-0">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="practice" data-testid="dw-tab-practice">{t("dw.tab_practice")}</TabsTrigger>
              <TabsTrigger value="insights" data-testid="dw-tab-insights">{t("dw.tab_insights")}</TabsTrigger>
              <TabsTrigger value="history" data-testid="dw-tab-history">{t("dw.tab_history")}</TabsTrigger>
            </TabsList>
            <TabsContent value="practice" className="mt-6"><PracticeTab t={t} summary={summary} /></TabsContent>
            <TabsContent value="insights" className="mt-6"><InsightsTab t={t} /></TabsContent>
            <TabsContent value="history" className="mt-6"><HistoryTab t={t} /></TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
